import React, { useState } from "react";
import { Button, Icon, IconButton, Tooltip } from "../../components/ui";
import { gapOf, money, shortGap } from "./dealsData";
import "../../stock/components/core-list-card.scss";
import "../../stock/components/core-list-kpi-strip.scss";

// Small building blocks shared by the three Deals tabs (design: "Deals Page" artifact).

/** Stock Alert's KPI strip (core-list-kpi): dot + label, big number, caption. tone: default|danger|warning|success|purple */
export const KpiStrip = ({ tiles }) => (
  <div className="core-list-kpi-strip">
    {tiles.map((t) => (
      <div key={t.label} className="core-list-kpi">
        <div className="core-list-kpi__label-row">
          <span className={`core-list-kpi__dot core-list-kpi__dot--${t.tone}`} aria-hidden="true" />
          <span className="core-list-kpi__label">{t.label}</span>
        </div>
        <div className="core-list-kpi__value">{t.value}</div>
        {t.sub ? <div className="core-list-kpi__caption">{t.sub}</div> : null}
      </div>
    ))}
  </div>
);

/**
 * One deal as a Stock Alert card (cl-card): photo with badges on it, reference / name / specs,
 * a price band, and a footer with who it is from and one action. Every Deals tab uses this,
 * so the cards stay identical across tabs.
 */
// `flag` sits in front of who / whoSub in the footer (Alex, 2026-11-02: in front of "Private
// seller · Chrono24", not the reference). `actions` replaces the default action buttons.
export const DealCard = ({ label, image, badges, flag = null, reference, refHref, name, specs, prices, who, whoSub, action, extra = null, actions = null }) => {
  const [failed, setFailed] = useState(false);
  return (
    <article className="cl-card deals-cl" aria-label={label}>
      <div className="cl-card__media">
        {image && !failed
          ? <img src={image} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
          : <span className="cl-card__media-empty" aria-hidden="true" />}
        {badges ? <span className="cl-card__found">{badges}</span> : null}
      </div>
      <div className="cl-card__body">
        {refHref
          ? <a className="cl-card__ref" href={refHref} target="_blank" rel="noopener noreferrer">{reference || "—"}</a>
          : <span className="cl-card__ref">{reference || "—"}</span>}
        <span className="cl-card__name">{name}</span>
        {specs ? <span className="cl-card__specs">{specs}</span> : null}
      </div>
      <div className="deals-cl__prices">{prices}</div>
      <div className="cl-card__foot">
        <span className="deals-cl__who-row">
          {flag}
          <span className="deals-cl__who">
            <span className="deals-cl__who-name" title={who}>{who}</span>
            {whoSub}
          </span>
        </span>
        <span className="cl-card__actions">
          {actions || <>
            {extra}
            {/* Beside a second control the link shrinks to an icon, so the seller line keeps its room. */}
            {extra
              ? <IconButton size="sm" label={action.label} title={action.label} href={action.href} target="_blank" rel="noopener noreferrer"><Icon name="ExternalLink" size="sm" /></IconButton>
              : <Button size="sm" variant="secondary" href={action.href} target="_blank" rel="noopener noreferrer">{action.label}</Button>}
          </>}
        </span>
      </div>
    </article>
  );
};

// The % pill's colour: 10%+ under green, 0–10% under amber, over muted (Stock Alert's colours).
const GAP_TONE = { hot: "deals-gap--hot", warm: "deals-gap--warm", cool: "deals-gap--cool" };

/**
 * Trade 12M | Market 12M side by side, small label over the figure ("—" when none), and the
 * price's % under / over the trade median as a pill beside it (Alex, 2026-11-02). Shared by the
 * Private sellers and Auctions cards so the two read the same. `gap` = gapPct(trade, price).
 */
export const Medians = ({ trade, market, gap }) => {
  const g = gapOf(gap);
  return (
    <span className="deals-cl__meds">
      <span className="deals-cl__med">
        <span className="deals-cl__med-k">Trade 12M</span>
        <span className="deals-cl__med-v">
          {money(trade)}
          {g ? <span className={`deals-gap ${GAP_TONE[g.tone]}`} title={`${g.label} the 12-month trade median`}>{shortGap(gap)}</span> : null}
        </span>
      </span>
      <span className="deals-cl__med"><span className="deals-cl__med-k">Market 12M</span><span className="deals-cl__med-v">{money(market)}</span></span>
    </span>
  );
};

/**
 * ⓘ beside a tab's search: hover / focus / long-press explains how listings get onto the tab and
 * how search works (Alex, 2026-10-29). `rows` = [label, value] pairs, one short fact each
 * ("too much text" — keep every value to a few words).
 */
export const DealsInfo = ({ title, rows }) => (
  <Tooltip side="bottom" className="deals-info-tip" content={(
    <span className="deals-info-tip__body">
      <strong className="deals-info-tip__title">{title}</strong>
      {rows.map(([k, v]) => (
        <span key={k} className="deals-info-tip__row">
          <span className="deals-info-tip__key">{k}</span>
          <span>{v}</span>
        </span>
      ))}
    </span>
  )}>
    <IconButton variant="ghost" round label={title} className="deals-info"><Icon name="Info" size="sm" /></IconButton>
  </Tooltip>
);

/** A tab's search field with the ⓘ beside it, for PageFilterBar's search slot. */
export const SearchWithInfo = ({ children, info }) => <div className="deals-search">{children}{info}</div>;

/** The card grid, Stock Alert's (cl-grid). */
export const DealGrid = ({ label, children }) => <ul className="cl-grid" aria-label={label}>{children}</ul>;
